import { Box, Typography } from "@mui/material";
import { useState, useMemo, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import HeaderBar from "../components/HeaderBar";
import MarketplaceFilters from "../components/MarketplaceFilters";
import MarketplaceProductCard from "../components/MarketplaceProductCard";
import MarketplaceTopBar from "../components/MarketplaceTopBar";
import BookDetailModal from "../components/BookDetailModal";
import ConferenceDetailModal from "../components/ConferenceDetailModal";
import PaginationControls from "../components/PaginationControls";
import { fetchBookPrices } from "../api/bookPricesApi";
import { fetchEquipmentPrices } from "../api/equipmentPricesApi";
import booksData from "../data/booksData.json";
import medicalEquipmentData from "../data/medicalEquipmentData.json";
import podcastData from "../data/podcastData.json";
import onlineEducationData from "../data/onlineEducationData.json";
import digitalAppsData from "../data/digitalAppsData.json";
import medicalJournalsData from "../data/medicalJournalsData.json";
import conferencesData from "../data/conferencesData.json";

const parsePrice = (value) => {
    if (value === null || value === undefined || value === "") return null;
    if (typeof value === "number" && Number.isFinite(value)) return value;
    const normalized = String(value).replace(/[^\d.]/g, "");
    if (!normalized) return null;
    const parsed = Number.parseFloat(normalized);
    return Number.isFinite(parsed) ? parsed : null;
};

const resolveJournalPrice = (journal) => {
    const online = parsePrice(journal.costOnline ?? journal["Cost - Online"]);
    const print = parsePrice(journal.costOnlineAndPrint ?? journal["Cost - Online & Print"]);
    const fallback = parsePrice(journal.price);

    if (online !== null && print !== null) return online;
    if (online !== null) return online;
    if (print !== null) return print;
    return fallback ?? 0;
};

const digitalAppProducts = (digitalAppsData.apps || []).map((app, index) => ({
    ...app,
    id: `digital-app-${app.id ?? index + 1}`,
    name: app.name || app.Name || "",
    category: "Digital Apps",
    image: app.image || "https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?w=400&h=300&fit=crop",
    price: Number(app.price ?? app.Cost ?? 0),
    purchaseLink: app.purchaseLink || app.Link || "",
    subcategory: app.category || app.Category || "",
    categories: [app.category || app.Category || ""].filter(Boolean),
}));

// Base book data from JSON (static metadata — live prices merged at runtime)
const baseBookProducts = booksData.books.map((book) => ({
    ...book,
    name: book.title,
    category: "Books",
    image: book.isbn ? `/images/books/${book.isbn}.jpg` : "/images/books/book-default.png",
}));

const buildEquipmentTitle = (item) => {
    const brandModel = [item.brand, item.model].filter(Boolean).join(" - ");
    return brandModel ? `${item.category} (${brandModel})` : item.category;
};

const extractAmazonAsin = (url) => {
    if (!url || !url.includes("amazon.com")) return null;
    const match = url.match(/\/dp\/([A-Z0-9]{10})/i);
    return match ? match[1].toUpperCase() : null;
};

// Base equipment data from JSON (static metadata — live Amazon prices merged at runtime).
// Display title follows the "Category (Brand - Model)" format when a model is available.
const baseEquipmentProducts = (medicalEquipmentData.equipment || []).map((item) => ({
    ...item,
    name: buildEquipmentTitle(item),
    title: buildEquipmentTitle(item),
    categories: [item.category],
    category: "Medical Equipment",
    image: `/images/medical-equipment/${item.image}.jpg`,
}));

const podcastProducts = podcastData.podcasts.map((podcast) => ({
    ...podcast,
    title: podcast.name,
    category: "Podcast",
    image: podcast.image || "/images/books/book-default.png",
}));

// Conference pricing depends on the viewer's profile designation:
// MD/DO see the physician rate, NP/PA see the APP rate (physician rate is the fallback).
const resolveConferencePrice = (conf, designation) => {
    const isAPP = designation === "NP" || designation === "PA";
    const price = isAPP ? conf.priceAPP : conf.pricePhysician;
    return Number(price ?? conf.price) || 0;
};

const buildConferenceProducts = (designation) =>
    (conferencesData.conferences || []).map((conf) => ({
        ...conf,
        title: conf.name,
        category: "Conferences",
        price: resolveConferencePrice(conf, designation),
        // purchaseLink powers the generic product card / fallbacks; registration is the CTA
        purchaseLink: conf.registrationLink || conf.conferenceLink || "",
        image: conf.image || "/images/books/book-default.png",
    }));

const onlineEducationProducts = onlineEducationData.items.map((item, index) => ({
    id: item.id || `oe-${index + 1}`,
    name: item.name,
    title: item.name,
    category: "Online Education",
    categories: [item.category],
    price: Number(item.cost) || 0,
    purchaseLink: item.link,
    image: item.image || "/images/books/book-default.png",
}));

const journalProducts = (medicalJournalsData.journals || []).map((journal, index) => ({
    ...journal,
    id: journal.id || `journal-${index + 1}`,
    name: journal.title || journal.name || "",
    category: "Medical Journals",
    price: resolveJournalPrice(journal),
    purchaseLink: journal.purchaseLink || journal["Purchase link"] || "",
    image: journal.image || "/images/books/book-default.png",
}));

// Price tier definitions matching the chip filters
const priceTierRanges = {
    under50: [0, 49.99],
    "50-200": [50, 200],
    "200-500": [200, 500],
    "500plus": [500, Infinity],
};

export default function MarketplacePage() {
    const [searchQuery, setSearchQuery] = useState("");
    const [selectedCategory, setSelectedCategory] = useState("All");
    const [selectedPriceTiers, setSelectedPriceTiers] = useState([]);
    const [sortBy, setSortBy] = useState("none");
    const [selectedSubcategories, setSelectedSubcategories] = useState([]);
    const [subSearchQuery, setSubSearchQuery] = useState("");
    const [selectedBook, setSelectedBook] = useState(null);
    const [page, setPage] = useState(1);
    const perPage = 12;
    const [bookPriceMap, setBookPriceMap] = useState({});
    const [equipmentPriceMap, setEquipmentPriceMap] = useState({});
    const { token, isAuthenticated } = useAuth();
    const [designation, setDesignation] = useState("");

    // Fetch the viewer's profile designation to pick conference pricing (MD/DO vs NP/PA)
    useEffect(() => {
        if (!isAuthenticated || !token) return;
        axios
            .get(`${process.env.REACT_APP_SERVER_URL}/api/profile`, {
                headers: { Authorization: `Bearer ${token}` },
            })
            .then((res) => setDesignation(res.data?.designation || ""))
            .catch(() => {}); // No profile — physician pricing shown by default
    }, [token, isAuthenticated]);

    const conferenceProducts = useMemo(() => buildConferenceProducts(designation), [designation]);

    // Fetch live book prices from backend
    useEffect(() => {
        fetchBookPrices()
            .then(({ prices }) => {
                const map = {};
                for (const p of prices) {
                    if (p.price != null) {
                        map[p.bookId] = { price: p.price, currency: p.currency, lastFetchedAt: p.lastFetchedAt };
                    }
                }
                setBookPriceMap(map);
            })
            .catch(() => {}); // Silently fail – books will show static price as fallback
    }, []);

    // Fetch live equipment prices from backend (Amazon-sold items only)
    useEffect(() => {
        fetchEquipmentPrices()
            .then(({ prices }) => {
                const map = {};
                for (const p of prices) {
                    if (p.price != null) {
                        map[p.equipmentId] = {
                            price: p.price,
                            currency: p.currency,
                            lastFetchedAt: p.lastFetchedAt,
                            asin: p.asin ? p.asin.toUpperCase() : null,
                        };
                    }
                }
                setEquipmentPriceMap(map);
            })
            .catch(() => {}); // Silently fail – equipment will show static price as fallback
    }, []);

    // Merge live prices into book products
    const bookProducts = useMemo(() =>
        baseBookProducts.map((book) => {
            const live = bookPriceMap[book.id];
            return {
                ...book,
                price: live ? live.price : book.price,
                lastFetchedAt: live ? live.lastFetchedAt : null,
                hasLivePrice: !!live,
            };
        }),
        [bookPriceMap]
    );

    // Merge live prices into equipment products
    const equipmentProducts = useMemo(() =>
        baseEquipmentProducts.map((item) => {
            const live = equipmentPriceMap[item.id];
            const currentAsin = extractAmazonAsin(item.purchaseLink);
            const hasMatchingLivePrice = !!(
                item.soldOnAmazon &&
                live &&
                currentAsin &&
                live.asin === currentAsin
            );
            return {
                ...item,
                price: hasMatchingLivePrice ? live.price : item.price,
                lastFetchedAt: hasMatchingLivePrice ? live.lastFetchedAt : null,
                hasLivePrice: hasMatchingLivePrice,
            };
        }),
        [equipmentPriceMap]
    );

    const allProducts = useMemo(() => [
        ...bookProducts.map((b) => ({ ...b, id: `book-${b.id}` })),
        ...journalProducts,
        ...digitalAppProducts,
        ...onlineEducationProducts,
        ...podcastProducts,
        ...conferenceProducts.map((c) => ({ ...c, id: `conference-${c.id}` })),
        ...equipmentProducts.map((e) => ({ ...e, id: `equipment-${e.id}` })),
    ], [bookProducts, conferenceProducts, equipmentProducts]);

    const isBookView = selectedCategory === "Books";
    const isPodcastView = selectedCategory === "Podcast";
    const isMedicalJournalsView = selectedCategory === "Medical Journals";
    const isDigitalAppsView = selectedCategory === "Digital Apps";
    const isOnlineEducationView = selectedCategory === "Online Education";
    const isConferenceView = selectedCategory === "Conferences";
    const isMedicalEquipmentView = selectedCategory === "Medical Equipment";
    const isCategoryFilterView = isBookView || isMedicalJournalsView || isDigitalAppsView || isOnlineEducationView || isPodcastView || isConferenceView || isMedicalEquipmentView;

    const sourceProducts = useMemo(() => {
        if (isBookView) return bookProducts;
        if (isMedicalJournalsView) return journalProducts;
        if (isDigitalAppsView) return digitalAppProducts;
        if (isOnlineEducationView) return onlineEducationProducts;
        if (isPodcastView) return podcastProducts;
        if (isConferenceView) return conferenceProducts;
        if (isMedicalEquipmentView) return equipmentProducts;
        if (selectedCategory === "All") return allProducts;
        return [];
    }, [selectedCategory, isBookView, isMedicalJournalsView, isDigitalAppsView, isOnlineEducationView, isPodcastView, isConferenceView, isMedicalEquipmentView, bookProducts, conferenceProducts, equipmentProducts, allProducts]);

    const filteredProducts = useMemo(() => {
        let result = sourceProducts;

        if (searchQuery) {
            const q = searchQuery.toLowerCase();
            result = result.filter(
                (p) =>
                    (p.title || p.name).toLowerCase().includes(q) ||
                    (p.category && p.category.toLowerCase().includes(q))
            );
        }

        if (selectedPriceTiers.length > 0) {
            result = result.filter((p) =>
                selectedPriceTiers.some((tier) => {
                    const [min, max] = priceTierRanges[tier];
                    return p.price >= min && p.price <= max;
                })
            );
        }

        if (isCategoryFilterView && selectedSubcategories.length > 0) {
            result = result.filter(
                (p) =>
                    p.categories && p.categories.some((cat) => selectedSubcategories.includes(cat))
            );
        }

        switch (sortBy) {
            case "price-asc":
                result = [...result].sort((a, b) => (a.price || 0) - (b.price || 0));
                break;
            case "price-desc":
                result = [...result].sort((a, b) => (b.price || 0) - (a.price || 0));
                break;
            case "title-asc":
                result = [...result].sort((a, b) =>
                    (a.title || a.name).localeCompare(b.title || b.name)
                );
                break;
            case "title-desc":
                result = [...result].sort((a, b) =>
                    (b.title || b.name).localeCompare(a.title || a.name)
                );
                break;
            default:
                break;
        }

        return result;
    }, [
        sourceProducts,
        searchQuery,
        selectedPriceTiers,
        selectedSubcategories,
        sortBy,
        isCategoryFilterView,
    ]);

    const totalPages = Math.ceil(filteredProducts.length / perPage);
    const paginatedProducts = filteredProducts.slice((page - 1) * perPage, page * perPage);

    const currentSubcategories = isBookView
        ? booksData.subcategories || []
        : isPodcastView
            ? podcastData.subcategories || []
            : isDigitalAppsView
                ? digitalAppsData.subcategories || []
                : isOnlineEducationView
                    ? onlineEducationData.subcategories || []
                    : isConferenceView
                        ? conferencesData.subcategories || []
                        : isMedicalEquipmentView
                            ? medicalEquipmentData.subcategories || []
                            : medicalJournalsData.subcategories || [];

    const handleToggleSubcategory = (cat) => {
        setSelectedSubcategories((prev) =>
            prev.includes(cat) ? prev.filter((c) => c !== cat) : [...prev, cat]
        );
        setPage(1);
    };

    const handleClearFilters = () => {
        setSelectedSubcategories([]);
        setSubSearchQuery("");
        setPage(1);
    };

    const handleTogglePriceTier = (tier) => {
        setSelectedPriceTiers((prev) =>
            prev.includes(tier) ? prev.filter((t) => t !== tier) : [...prev, tier]
        );
        setPage(1);
    };

    const handleClearAll = () => {
        setSearchQuery("");
        setSelectedCategory("All");
        setSelectedPriceTiers([]);
        setSortBy("none");
        setSelectedSubcategories([]);
        setSubSearchQuery("");
        setPage(1);
    };

    const handleCategoryChange = (cat) => {
        setSelectedCategory(cat);
        setSelectedSubcategories([]);
        setSubSearchQuery("");
        setPage(1);
    };

    const handleViewDetails = (product) => {
        // For online education and podcasts, go directly to purchase link
        if ((product.category === "Online Education" || product.category === "Podcast") && product.purchaseLink) {
            window.open(product.purchaseLink, "_blank", "noopener,noreferrer");
            return;
        }

        // For everything else, open the detail modal
        setSelectedBook(product);
    };

    const currentFilterTitle = isBookView
        ? "Filter Books"
        : isPodcastView
            ? "Filter Podcasts"
            : isDigitalAppsView
                ? "Filter Digital Apps"
                : isOnlineEducationView
                    ? "Filter Online Education"
                    : isConferenceView
                        ? "Filter Conferences"
                        : isMedicalEquipmentView
                            ? "Filter Medical Equipment"
                            : "Filter Medical Journals";

    return (
        <Box sx={{ minHeight: "100vh", backgroundColor: "#f6f7f9" }}>
            <HeaderBar />

            <Box sx={{ textAlign: "center", pt: 4, pb: 3, px: 2 }}>
                <Typography variant="h3" fontWeight="bold" color="#2C3E50" mb={1}>
                    CME Marketplace
                </Typography>
                <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ fontSize: "0.9375rem", color: "#6c757d" }}
                >
                    Browse conferences, journals, books, digital apps, podcasts, medical equipment, and more!
                </Typography>
            </Box>

            <Box
                sx={{
                    maxWidth: 1280,
                    mx: "auto",
                    px: { xs: 2, sm: 3, md: 4 },
                    pb: 4,
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: { xs: "column", md: "row" },
                        gap: 3,
                    }}
                >
                    {isCategoryFilterView && (
                        <Box
                            sx={{
                                width: { xs: "100%", md: 240 },
                                flexShrink: 0,
                            }}
                        >
                            <MarketplaceFilters
                                filterTitle={currentFilterTitle}
                                subcategories={[...currentSubcategories].sort((a, b) => a.localeCompare(b))}
                                selectedSubcategories={selectedSubcategories}
                                onToggleSubcategory={handleToggleSubcategory}
                                onClearFilters={handleClearFilters}
                                subSearchQuery={subSearchQuery}
                                setSubSearchQuery={setSubSearchQuery}
                            />
                        </Box>
                    )}

                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <MarketplaceTopBar
                            searchQuery={searchQuery}
                            setSearchQuery={setSearchQuery}
                            selectedCategory={selectedCategory}
                            setSelectedCategory={handleCategoryChange}
                            selectedPriceTiers={selectedPriceTiers}
                            onTogglePriceTier={handleTogglePriceTier}
                            sortBy={sortBy}
                            setSortBy={setSortBy}
                            onClear={handleClearAll}
                        />

                        <Box sx={{ mt: 2, mb: 1.5 }}>
                            <Typography variant="body2" sx={{ color: "#6c757d", fontSize: "0.875rem" }}>
                                {filteredProducts.length} results
                            </Typography>
                        </Box>

                        <Box
                            sx={{
                                display: "grid",
                                gridTemplateColumns: {
                                    xs: "1fr",
                                    sm: "repeat(2, 1fr)",
                                    md: isCategoryFilterView ? "repeat(3, 1fr)" : "repeat(4, 1fr)",
                                    lg: "repeat(4, 1fr)",
                                },
                                gap: 2,
                            }}
                        >
                            {paginatedProducts.map((product) => (
                                <MarketplaceProductCard
                                    key={product.id}
                                    product={product}
                                    onViewDetails={handleViewDetails}
                                />
                            ))}
                        </Box>

                        {filteredProducts.length === 0 && (
                            <Box
                                sx={{
                                    textAlign: "center",
                                    py: 8,
                                    color: "text.secondary",
                                }}
                            >
                                <Typography variant="h6" sx={{ mb: 1 }}>
                                    No products found
                                </Typography>
                                <Typography variant="body2">
                                    Try adjusting your filters or search terms.
                                </Typography>
                            </Box>
                        )}

                        <PaginationControls page={page} totalPages={totalPages} setPage={setPage} />
                    </Box>
                </Box>
            </Box>

            <BookDetailModal
                book={selectedBook?.category !== "Conferences" ? selectedBook : null}
                open={!!selectedBook && selectedBook.category !== "Conferences"}
                onClose={() => setSelectedBook(null)}
            />
            <ConferenceDetailModal
                conference={selectedBook?.category === "Conferences" ? selectedBook : null}
                open={!!selectedBook && selectedBook.category === "Conferences"}
                onClose={() => setSelectedBook(null)}
            />
        </Box>
    );
}
